import type { Error as DomainError } from '@/lib/api/generated/model';

import { reportThreadProblem } from './report';

/** What was being done on the thread when it was refused. */
export type ThreadAction = 'post' | 'retry' | 'add';

/**
 * A refused thread write, as one sentence (spec 149, service spec 131's error
 * codes). `code` is the service's, kept so a caller can treat one refusal
 * differently — `ALREADY_ADDED` is an answer, not a failure — without reading
 * the sentence.
 */
export interface ThreadRefusal {
  code: string | null;
  message: string;
}

/** The sentence when no code is recognised: the action, said plainly. */
const FALLBACK: Record<ThreadAction, string> = {
  post: 'The message could not be sent.',
  retry: 'The reply could not be retried.',
  add: 'The message could not be added to the task’s context.',
};

/**
 * The service's codes, in the product's words. Branching on `error`, not on
 * status: `RUN_NOT_FINISHED`, `REPLY_NOT_RETRYABLE` and `ALREADY_ADDED` all
 * answer 409, and both throttles answer 429.
 */
const SENTENCE: Partial<Record<string, string>> = {
  RUN_NOT_FOUND: 'This run no longer exists.',
  THREAD_MESSAGE_NOT_FOUND: 'That message no longer exists.',
  RUN_NOT_FINISHED: 'You can ask once the run has finished.',
  RUN_NOT_ASKABLE: 'This run had no agent to ask.',
  REPLY_NOT_RETRYABLE:
    'That reply can’t be retried: it is not failed or refused.',
  ALREADY_ADDED: 'That message is already in the task’s context.',
  THREAD_QUEUE_FULL:
    'Five questions are already waiting on this run. Ask again once the agent has answered.',
  THREAD_RATE_LIMITED:
    'You’ve asked a lot in a short time. Try again in a minute.',
};

/**
 * The domain envelope, when a refusal carries one. A malformed request's 422 is
 * FastAPI's validation body instead (`detail`, no `error`): a client bug, not a
 * refusal, so it reads as the action's plain failure.
 */
function domainErrorOf(body: unknown): DomainError | undefined {
  return typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof body.error === 'string'
    ? { error: body.error }
    : undefined;
}

export function classifyThreadRefusal(
  action: ThreadAction,
  body: unknown,
  status?: number,
): ThreadRefusal {
  const code = domainErrorOf(body)?.error ?? null;
  const sentence = code ? SENTENCE[code] : undefined;
  if (sentence === undefined) {
    // A code this client has no words for, or no code at all: the person reads
    // the action's plain failure, and this says what it was (review of !252).
    reportThreadProblem('refusal not recognised', { action, status, code });
  }
  return { code, message: sentence ?? FALLBACK[action] };
}

/**
 * A refused thread write, as a rejection, carrying its classified refusal so a
 * mutation's `error` is typed rather than a bare `Error` (constitution §2.5).
 */
export class ThreadRefusalError extends Error {
  readonly refusal: ThreadRefusal;

  constructor(refusal: ThreadRefusal) {
    super(refusal.message);
    this.name = 'ThreadRefusalError';
    this.refusal = refusal;
  }
}
